import React, { useState, useMemo, useEffect, useRef } from "react";
import { Form, Modal, Button, Row, Image } from "react-bootstrap";
import ResponsiveTable from "../Grid/ResponsiveTable";
import InputBox from "../common/InputBox";
import SearchSelect from "../common/SearchSelect";
import Switch from "../common/Switch";
import editIcon from "../../assets/icons/EditIcon.svg";
import deleteIcon from "../../assets/icons/deleteA.svg";
import xclose from "../../assets/icons/x-close.svg";
import { getSalesManagerRole } from "../../services/isob-service";
import Alert from "../common/Alert";

const PG_DETAILS_HEADERS = [
    { columnName: "solutionOffering", displayName: "Solution Offering", displayIndication: true, dataType: "text", width: 170,required:true },
    { columnName: "bomPrice", displayName: "BOM Price (INR)", displayIndication: true, dataType: "text", width: 170,validation: "isNumericWithDecimal" },
    { columnName: "discountOnBomPrice", displayName: "Discount on BOM Price %", displayIndication: true, dataType: "text", width: 220 },
    { columnName: "cobp", displayName: "COBP (INR)", displayIndication: true, dataType: "text", width: 140 },
    { columnName: "dealerMargin", displayName: "Dealer Margin %", displayIndication: true, dataType: "text", width: 170 },
    { columnName: "dobp", displayName: "DOBP (INR)", displayIndication: true, dataType: "text", width: 140 },
];

const calcDiscountOnBom = (bomPrice, cobp) => {
    const bom = parseFloat(bomPrice);
    const c = parseFloat(cobp);
    if (!bom || !c || bom === 0) return "";
    return (((bom - c) / bom) * 100).toFixed(2);
};

const calcDealerMargin = (cobp, dobp) => {
    const c = parseFloat(cobp);
    const d = parseFloat(dobp);
    if (!c || !d || c === 0) return "";
    return (((c - d) / c) * 100).toFixed(2);
};

export default function IsobTab3({ formValues, handleInputChange, error, setError, mandatoryFields, setMandatoryFields, getDropDownOptions,orderBookingFormData ,getSalesRoleOptions, discountRecord, readOnly}) {
    console.log("orderBookingFormData",orderBookingFormData);
    const [pgDetails, setPgDetails] = useState(formValues.pgDetails || []);
    const prevDealerSAC = useRef(formValues.DealerSAC_applicable);
    const prevLD = useRef(formValues.LD_applicable);
    const prevCreditSharing = useRef(formValues.creditSharing);
    const prevOverallOutflow = useRef(formValues.Overall_Outflow);
    const prevFinalBasicPrice = useRef(formValues.FinalBasicPrice);

    useEffect(() => {
        setPgDetails(formValues.pgDetails || []);
    }, [formValues.pgDetails, readOnly]);

    useEffect(() => {
        if (readOnly) {
            setMandatoryFields((prev) => prev.filter((f) => f !== "pgDetails"));
            return;
        }
        setMandatoryFields((prev) =>
            pgDetails.length === 0
                ? prev.includes("pgDetails") ? prev : [...prev, "pgDetails"]
                : prev.filter((f) => f !== "pgDetails")
        );
    }, [pgDetails, readOnly]);

    // Mandatory validation for payment fields and LD/Dealer SAC selection
    useEffect(() => {
        if (readOnly) {
            const managedFields = ["Advance", "againstDeliveryPercent", "againstInstallationPercent", "LD_applicable", "DealerSAC_applicable", "LD_min", "ldMax", "Dealer_SAC", "DealerSAC_value", "DealerCode"];
            setMandatoryFields((prev) => prev.filter((f) => !managedFields.includes(f)));
            return;
        }
        const missing = [];

        // if (!formValues.LD_applicable) missing.push("LD_applicable");
        // if (!formValues.DealerSAC_applicable) missing.push("DealerSAC_applicable");
        if (formValues.LD_applicable === "Yes") {
            if (!formValues.LD_min) missing.push("LD_min");
            if (!formValues.ldMax) missing.push("ldMax");
        }
        if (formValues.DealerSAC_applicable === "Yes") {
            if (!formValues.Dealer_SAC) missing.push("Dealer_SAC");
            if (!formValues.DealerSAC_value) missing.push("DealerSAC_value");
            if (!formValues.DealerCode) missing.push("DealerCode");
        }
        const managedFields = ["Advance", "againstDeliveryPercent", "againstInstallationPercent", "LD_applicable", "DealerSAC_applicable", "LD_min", "ldMax", "Dealer_SAC", "DealerSAC_value", "DealerCode"];
        setMandatoryFields((prev) => {
            const filtered = prev.filter((f) => !managedFields.includes(f));
            return [...filtered, ...missing];
        });
    }, [formValues.Advance, formValues.againstDeliveryPercent, formValues.againstInstallationPercent, formValues.LD_applicable, formValues.DealerSAC_applicable, formValues.LD_min, formValues.ldMax, formValues.Dealer_SAC, formValues.DealerSAC_value, formValues.DealerCode]);

    // Clear dealer SAC fields when not applicable
    useEffect(() => {
        if (prevDealerSAC.current === formValues.DealerSAC_applicable) return;
        prevDealerSAC.current = formValues.DealerSAC_applicable;
        if (formValues.DealerSAC_applicable !== "Yes") {
            setError((prev) => {
                const updated = { ...prev };
                delete updated.Dealer_SAC;
                delete updated.DealerSAC_value;
                delete updated.DealerCode;
                delete updated.Sharing_Branch;
                delete updated.Sharing_value;
                delete updated.SM;
                return updated;
            });
            handleInputChange({ target: { name: "__bulk__", value: { Dealer_SAC: "", DealerSAC_value: "", DealerCode: "", Order_closing: "", enquiryGeneration: "", paymentCollection: "", installation: "", creditSharing: "",Sharing_Branch: "",Sharing_value: "",SM: "" } } });
        }
    }, [formValues.DealerSAC_applicable]);

    useEffect(() => {
    if (prevCreditSharing.current === formValues.creditSharing) return;
    prevCreditSharing.current = formValues.creditSharing;
    if (readOnly) return;
    if (!formValues.creditSharing) {
        setError((prev) => {
            const updated = { ...prev };
            delete updated.Sharing_Branch;
            delete updated.Sharing_value;
            delete updated.SM;
            return updated;
        });
        handleInputChange({ 
            target: { 
                name: "__bulk__", 
                value: { 
                    Sharing_Branch: "",
                    Sharing_value: "",
                    SM: ""
                } 
            } 
        });
    }
}, [formValues.creditSharing]);

    // Clear LD fields when not applicable
    useEffect(() => {
        if (prevLD.current === formValues.LD_applicable) return;
        prevLD.current = formValues.LD_applicable;
        if (readOnly) return;
        if (formValues.LD_applicable !== "Yes") {
            setError((prev) => {
                const updated = { ...prev };
                delete updated.LD_min;
                delete updated.ldMax;
                return updated;
            });
            handleInputChange({ target: { name: "__bulk__", value: { LD_min: "", ldMax: "", deliveryStartDate: "", deliveryLDPerWeek: "", deliveryEndDate: "", deliveryMaxLD: "", instalStartDate: "", instalLDPerWeek: "", instalEndDate: "", instalMaxLD: "" } } });
        }
    }, [formValues.LD_applicable]);

    // PG Details Modal
    const [showPgModal, setShowPgModal] = useState(false);
    const [pgForm, setPgForm] = useState({ solutionOffering: "", bomPrice: "", cobp: "", dobp: "" });
    const [editPgIdx, setEditPgIdx] = useState(null);
   

    const buildPgRow = (form) => ({
        ...form,
        discountOnBomPrice: calcDiscountOnBom(form.bomPrice, form.cobp),
        dealerMargin: calcDealerMargin(form.cobp, form.dobp),
    });

    const syncPgDetails = (updated) => {
        handleInputChange({ target: { name: "pgDetails", value: updated } });
    };

    const handleAddPg = () => {
        setPgForm({ solutionOffering: "", bomPrice: "", cobp: "", dobp: "" });
        setEditPgIdx(null);
        setShowPgModal(true);
    };

    const handleEditPg = (item) => {
        const idx = pgDetails.findIndex((p) => p === item);
        setPgForm({ solutionOffering: item.solutionOffering, bomPrice: item.bomPrice, cobp: item.cobp, dobp: item.dobp });
        setEditPgIdx(idx);
        setShowPgModal(true);
    };

    const handleDeletePg = (item) => {
        syncPgDetails(pgDetails.filter((p) => p !== item));
        Alert('success', 'PG details deleted successfully.');
    };

    const handleSavePg = () => {
        const row = buildPgRow(pgForm);
        if (editPgIdx !== null) {
            const updated = pgDetails.map((p, i) => i === editPgIdx ? row : p);
            syncPgDetails(updated);
            Alert('success', 'PG details updated successfully.');
        } else {
            syncPgDetails([...pgDetails, row]);
            Alert('success', 'PG details added successfully.');
        }
        setShowPgModal(false);
    };

    const closePgModal = (e) => {
        e.preventDefault();
        setShowPgModal(false);
        setError((prev) => ({ ...prev, solutionOffering: "", bomPrice: "", cobp: "", dobp: "" }))
    };

    const pgIcons = readOnly ? [] : [
        { src: editIcon, click: handleEditPg },
        { src: deleteIcon, click: handleDeletePg },
    ];

    // Commercial Details - Auto calculations
    const commercialSummary = useMemo(() => {
        const bomPrice = pgDetails.reduce((sum, r) => sum + (parseFloat(r.bomPrice) || 0), 0);
        const customerOrderBasicPrice = pgDetails.reduce((sum, r) => sum + (parseFloat(r.cobp) || 0), 0);
        const dealerOrderBasicPrice = pgDetails.reduce((sum, r) => sum + (parseFloat(r.dobp) || 0), 0);

        // Final Basic Price = Sum of DOBP or COBP (use DOBP if available, else COBP)
        const finalBasicPrice = dealerOrderBasicPrice;

        const cobpDiscount = bomPrice ? (((bomPrice - customerOrderBasicPrice) / bomPrice) * 100).toFixed(2) : "0";
        const dealerDiscount = customerOrderBasicPrice ? (((customerOrderBasicPrice - dealerOrderBasicPrice) / customerOrderBasicPrice) * 100).toFixed(2) : "0";
        const overallOutflow = bomPrice ? (((bomPrice - finalBasicPrice) / bomPrice) * 100).toFixed(2) : "0";

        const serviceTax = formValues.serviceType !== "Special Economic Zone" ? (finalBasicPrice * 18 / 100) : 0;
        const supplyTax = formValues.supplyType !== "Special Economic Zone" ? (finalBasicPrice * 18 / 100) : 0;
        const priceAfterTax = (finalBasicPrice + (formValues.serviceType ? serviceTax : 0)).toFixed(2);
       

        return {
            bomPrice: bomPrice.toFixed(2),
            customerOrderBasicPrice: customerOrderBasicPrice.toFixed(2),
            dealerOrderBasicPrice: dealerOrderBasicPrice.toFixed(2),
            finalBasicPrice: finalBasicPrice.toFixed(2),
            cobpDiscount,
            dealerDiscount,
            overallOutflow,
            serviceTax: serviceTax.toFixed(2),
            supplyTax: supplyTax.toFixed(2),
            priceAfterTax,
            approvedDiscount: (discountRecord?.field_16 * 100) || "0",
        }
    }, [pgDetails, formValues.serviceType,formValues.supplyType ]);


useEffect(() => {
    if (
        prevOverallOutflow.current === commercialSummary.overallOutflow &&
        prevFinalBasicPrice.current === commercialSummary.finalBasicPrice
    ) return;
    prevOverallOutflow.current = commercialSummary.overallOutflow;
    prevFinalBasicPrice.current = commercialSummary.finalBasicPrice;
    handleInputChange({ 
        target: { 
            name: "__bulk__", 
            value: { 
                Overall_Outflow: commercialSummary.overallOutflow,
                FinalBasicPrice: commercialSummary.finalBasicPrice,
                OrderValueLakhs: commercialSummary.finalBasicPrice
            } 
        } 
    });
}, [commercialSummary.overallOutflow, commercialSummary.finalBasicPrice, handleInputChange]);

    // Payment Details - Auto calculations (% * FBP)
    const paymentCalc = useMemo(() => {
        const fbp = parseFloat(commercialSummary.finalBasicPrice) || 0;
        const adv = parseFloat(formValues.Advance) || 0;
        const del = parseFloat(formValues.againstDeliveryPercent) || 0;
        const inst = parseFloat(formValues.againstInstallationPercent) || 0;
        const totalPercent = (adv + del + inst).toFixed(2);
        console.log("totalpercent",totalPercent)
        return {
            advanceINR: ((adv / 100) * fbp).toFixed(2),
            againstDeliveryINR: ((del / 100) * fbp).toFixed(2),
            againstInstallationINR: ((inst / 100) * fbp).toFixed(2),
            totalPercent,
            totalINR: fbp.toFixed(2),
            totalPercentError: parseFloat(totalPercent) > 0 && parseFloat(totalPercent) !== 100
    ? parseFloat(totalPercent) > 100
        ? "Total payment percentage cannot exceed 100%"
        : "Total payment percentage must equal 100%"
    : "",

    
        };
    }, [formValues.Advance, formValues.againstDeliveryPercent, formValues.againstInstallationPercent, commercialSummary.finalBasicPrice]);

        useEffect(() => {
    setError((prev) => ({ ...prev, totalPaymentPercent: paymentCalc.totalPercentError }));
}, [paymentCalc.totalPercentError]);


    console.log("mandatoryfields",mandatoryFields);
    console.log("Orderbooking data in tab 3",orderBookingFormData);
        return (
        <div className="form-right-adjust">

            {/* PG Details */}
            <div className="ordorder-info-section-plain-verticalpad add-edit-form isob-table">
                <div className="isob-table-header">
                    <h6 className="section-subheading isob-table-heading">PG Details</h6>
                    {!readOnly && (
                        <span className="isob-add-link" onClick={handleAddPg}>
                            &#43; Add PG Details
                        </span>
                    )}
                </div>
                <div className="pg-table-with-total">
                    <ResponsiveTable
                        sessionName="PG Details"
                        headers={PG_DETAILS_HEADERS}
                        data={pgDetails}
                        icons={pgIcons}
                        handleAdd={handleAddPg}
                        dataType="pgDetails"
                        noCheckbox={true}
                        paginationView="false"
                        DisplayExcelDownload={false}
                        searchmd={6}
                        noSearch={true}
                        noScroll={true}
                        noEmptyState={true}
                        noHeader={true}
                    />
                    {pgDetails.length > 0 && (
                        <table className="pg-total-row responsiveTable">
                            <tbody>
                                <tr className="table-border">
                                    <td style={{ width: 8, minWidth: 8, padding: 0 }}></td>
                                    <td style={{ width: 170 ,color: '#333333' }}><div style={{paddingLeft: '10px'}}>Total</div></td>
                                    <td style={{ width: 170 }}><div style={{paddingLeft: '10px'}}>{commercialSummary.bomPrice}</div></td>
                                    <td style={{ width: 220 }}><div style={{paddingLeft: '10px'}}>{commercialSummary.cobpDiscount}%</div></td>
                                    <td style={{ width: 140 }}><div style={{paddingLeft: '10px'}}>{commercialSummary.customerOrderBasicPrice}</div></td>
                                    <td style={{ width: 170 }}><div style={{paddingLeft: '10px'}}>{commercialSummary.dealerDiscount}%</div></td>
                                    <td style={{ width: 140 }}><div style={{paddingLeft: '10px'}}>{commercialSummary.dealerOrderBasicPrice}</div></td>
                                    <td className="fixed icon" style={{ right: 0, minWidth: 70, backgroundColor: '#f9f2f6', borderRight: 'none', borderRadius: '0' }}></td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
                {pgDetails.length > 0 && (
                    <div className="pg-total-mobile" style={{ display: "none" }}>
                        <div className="pg-total-mobile-title">Total</div>
                        <div className="pg-total-mobile-item">
                            <span className="pg-total-mobile-label">BOM Price (INR)</span>
                            <span className="pg-total-mobile-value">{commercialSummary.bomPrice}</span>
                        </div>
                        <div className="pg-total-mobile-item">
                            <span className="pg-total-mobile-label">Discount on BOM Price %</span>
                            <span className="pg-total-mobile-value">{commercialSummary.cobpDiscount}%</span>
                        </div>
                        <div className="pg-total-mobile-item">
                            <span className="pg-total-mobile-label">COBP (INR)</span>
                            <span className="pg-total-mobile-value">{commercialSummary.customerOrderBasicPrice}</span>
                        </div>
                        <div className="pg-total-mobile-item">
                            <span className="pg-total-mobile-label">Dealer Margin %</span>
                            <span className="pg-total-mobile-value">{commercialSummary.dealerDiscount}%</span>
                        </div>
                        <div className="pg-total-mobile-item">
                            <span className="pg-total-mobile-label">DOBP (INR)</span>
                            <span className="pg-total-mobile-value">{commercialSummary.dealerOrderBasicPrice}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Commercial Details Summary */}
            <div className="add-edit-form">
                <h6 className="section-subheading">Commercial Details</h6>
                <div className="commercial-details-grid">
                    {/* Row 1 */}
                    <div className="commercial-details-row">
                        <div className="commercial-details-cell">
                            <SearchSelect
                                name="supplyType"
                                value={formValues.supplyType || ""}
                                option={getDropDownOptions ? getDropDownOptions("GST") : []}
                                label="Supply Type"
                                id="supplyType"
                                fieldName="Supply Type"
                                onChange={handleInputChange}
                                placeholder=""
                                setError={setError}
                                mandatoryFields={mandatoryFields}
                                setMandatoryFields={setMandatoryFields}
                                readOnly={readOnly}
                            />
                        </div>
                        <div className="commercial-details-cell">
                            <span className="commercial-label">Supply tax</span>
                            <span className="commercial-value">{formValues.supplyType ? commercialSummary.supplyTax : 0}</span>
                        </div>
                        <div className="commercial-details-cell">
                            <span className="commercial-label">Approved Discount %</span>
                            <span className="commercial-value">{(discountRecord?.field_16 * 100) || "0"}</span>
                        </div>
                        <div className="commercial-details-cell" style={{backgroundColor:"#f9f2f6"}}>
                            <span className="commercial-label">Final Basic Price</span>
                            <span className="commercial-value">{commercialSummary.finalBasicPrice}</span>
                        </div>
                    </div>
                    {/* Row 2 */}
                    <div className="commercial-details-row">
                        <div className="commercial-details-cell">
                            <SearchSelect
                                name="serviceType"
                                value={formValues.serviceType || ""}
                                option={getDropDownOptions ? getDropDownOptions("GST") : []}
                                label="Service Type"
                                id="serviceType"
                                fieldName="Service Type"
                                onChange={handleInputChange}
                                placeholder=""
                                setError={setError}
                                mandatoryFields={mandatoryFields}
                                setMandatoryFields={setMandatoryFields}
                                readOnly={readOnly}
                            />
                        </div>
                        <div className="commercial-details-cell">
                            <span className="commercial-label">Service tax</span>
                            <span className="commercial-value">{formValues.serviceType ? commercialSummary.serviceTax : 0}</span>
                        </div>
                         <div className="commercial-details-cell">
                            <span className="commercial-label">Overall Outflow %</span>
                            <span className="commercial-value">{commercialSummary.overallOutflow}</span>
                        </div>
                        <div className="commercial-details-cell" style={{backgroundColor:"#f9f2f6"}}>
                            <span className="commercial-label">Price after tax</span>
                            <span className="commercial-value">{commercialSummary.priceAfterTax}</span>
                        </div>                    
                    </div>
                </div>
            </div>

            {/* Dealer SAC Section */}
            <div className="add-edit-form isob-dealer-sac-section">
                <div className="order-info-section-colored-norightmar">
                    <div className="isob-dealer-sac-radio-row">
                        <Form.Label className="isob-dealer-sac-label">Dealer SAC Applicable *</Form.Label>
                        <Form.Check
                            inline
                            type="radio"
                            label="Yes"
                            name="DealerSAC_applicable"
                            value="Yes"
                            checked={formValues.DealerSAC_applicable === "Yes"}
                            onChange={handleInputChange}
                            disabled={readOnly}
                        />
                        <Form.Check
                            inline
                            type="radio"
                            label="No"
                            name="DealerSAC_applicable"
                            value="No"
                            checked={formValues.DealerSAC_applicable !== "Yes"}
                            onChange={handleInputChange}
                            disabled={readOnly}
                        />
                    </div>

                    {formValues.DealerSAC_applicable === "Yes" && (
                        <>
                            <Row className="modal-row  dealersac-input">
                                <InputBox
                                    label="Dealer SAC % *"
                                    name="Dealer_SAC"
                                    id="Dealer_SAC"
                                    type="text"
                                    value={formValues.Dealer_SAC || ""}
                                    onChange={handleInputChange}
                                    error={error?.Dealer_SAC}
                                    setError={setError}
                                    required={!readOnly}
                                    readOnly={readOnly}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName="Dealer SAC %"
                                    isNumericWithDecimal
                                    maxLength={10}
                                />
                                <InputBox
                                    label="Dealer SAC (INR) *"
                                    name="DealerSAC_value"
                                    id="DealerSAC_value"
                                    type="text"
                                    value={formValues.DealerSAC_value || ""}
                                    onChange={handleInputChange}
                                    error={error?.DealerSAC_value}
                                    setError={setError}
                                    required={!readOnly}
                                    readOnly={readOnly}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName="Dealer SAC (INR)"
                                    isNumericWithDecimal
                                    maxLength={10}
                                />
                                <InputBox
                                    label="Dealer Code *"
                                    name="DealerCode"
                                    id="DealerCode"
                                    type="text"
                                    value={formValues.DealerCode || ""}
                                    onChange={handleInputChange}
                                    error={error?.DealerCode}
                                    setError={setError}
                                    required={!readOnly}
                                    readOnly={readOnly}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName="Dealer Code"
                                    isAlphanumericWithSpecialChars
                                    maxLength={20}
                                />
                            </Row>
                            <div className="dealer-sac-toggles">
                                <div className="dealer-sac-toggle-item">
                                    <Form.Label className="dealer-sac-toggle-label">Order Closing</Form.Label>
                                    <Switch
                                        isOn={!!formValues.Order_closing}
                                        handleToggle={(e) => handleInputChange({ target: { name: "Order_closing", value: e.target.checked } })}
                                        index="Order_closing"
                                        readOnly={readOnly}
                                    />
                                </div>
                                <div className="dealer-sac-toggle-item">
                                    <Form.Label className="dealer-sac-toggle-label">Enquiry Generation</Form.Label>
                                    <Switch
                                        isOn={!!formValues.enquiryGeneration}
                                        handleToggle={(e) => handleInputChange({ target: { name: "enquiryGeneration", value: e.target.checked } })}
                                        index="enquiryGeneration"
                                        readOnly={readOnly}
                                    />
                                </div>
                                <div className="dealer-sac-toggle-item">
                                    <Form.Label className="dealer-sac-toggle-label">Payment Collection</Form.Label>
                                    <Switch
                                        isOn={!!formValues.paymentCollection}
                                        handleToggle={(e) => handleInputChange({ target: { name: "paymentCollection", value: e.target.checked } })}
                                        index="paymentCollection"
                                        readOnly={readOnly}
                                    />
                                </div>
                                <div className="dealer-sac-toggle-item">
                                    <Form.Label className="dealer-sac-toggle-label">Installation</Form.Label>
                                    <Switch
                                        isOn={!!formValues.installation}
                                        handleToggle={(e) => handleInputChange({ target: { name: "installation", value: e.target.checked } })}
                                        index="installation"
                                        readOnly={readOnly}
                                    />
                                </div>
                                <div className="dealer-sac-toggle-item">
                                    <Form.Label className="dealer-sac-toggle-label">Credit Sharing</Form.Label>
                                    <Switch
                                        isOn={!!formValues.creditSharing}
                                        handleToggle={(e) => handleInputChange({ target: { name: "creditSharing", value: e.target.checked } })}
                                        index="creditSharing"
                                        readOnly={readOnly}
                                    />
                                </div>
                            </div>
                            {formValues.creditSharing && (
                                <div className="credit-sharing-fields">
        <h6 className="section-subheading-plain">Credit Sharing Details</h6>
        <Row className="modal-row add-edit-form">
            <InputBox
                label="Sharing Branch"
                name="Sharing_Branch"
                id="Sharing_Branch"
                type="text"
                value={formValues.Sharing_Branch || ""}
                onChange={handleInputChange}
                error={error?.Sharing_Branch}
                setError={setError}
                required={false}
                readOnly={readOnly}
                mandatoryFields={mandatoryFields}
                setMandatoryFields={setMandatoryFields}
                fieldName="Sharing Branch"
                maxLength={50}
            />
            <InputBox
                label="Sharing Value"
                name="Sharing_value"
                id="Sharing_value"
                type="text"
                value={formValues.Sharing_value || ""}
                onChange={handleInputChange}
                error={error?.Sharing_value}
                setError={setError}
                required={false}
                readOnly={readOnly}
                mandatoryFields={mandatoryFields}
                setMandatoryFields={setMandatoryFields}
                fieldName="Sharing Value"
                isNumericWithDecimal
                maxLength={10}
            />
            <SearchSelect
                name="SM"
                value={formValues.SM || ""}
                option={getSalesRoleOptions ? getSalesRoleOptions() : []}
                label="Sales Manager"
                id="SM"
                fieldName="Sales Manager"
                onChange={handleInputChange}
                placeholder="Select Sales Manager"
                setError={setError}
                error={error?.SM}
                mandatoryFields={mandatoryFields}
                setMandatoryFields={setMandatoryFields}
                readOnly={readOnly}
            />
        </Row>
    </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Payment Details */}
            <div className="add-edit-form isob-payment-details-section">
                <h6 className="section-subheading">Payment details</h6>
                <div className="payment-details-table">
                    <div className="payment-details-header">
                        <span className="payment-col-item">Payment Items</span>
                        <span className="payment-col-percent">Value in Percentage</span>
                        <span className="payment-col-value">Value in INR</span>
                    </div>
                    <div className="payment-details-row">
                        <span className="payment-col-item">Advance</span>
                        <div className="payment-col-percent">
                            <InputBox
                                label="Advance %"
                                name="Advance"
                                id="Advance"
                                type="text"
                                value={formValues.Advance || "0"}
                                onChange={handleInputChange}
                                error={error?.Advance}
                                setError={setError}
                                required={false}
                                readOnly={true}
                                mandatoryFields={mandatoryFields}
                                setMandatoryFields={setMandatoryFields}
                                fieldName="Advance %"
                                isNumericWithDecimal
                                placeholder="Enter value"
                            />
                        </div>
                        <span className="payment-col-value">{paymentCalc.advanceINR}</span>
                    </div>
                    <div className="payment-details-row">
                        <span className="payment-col-item">Against delivery*</span>
                        <div className="payment-col-percent">
                            <InputBox
                                label="Against Delivery %"
                                name="againstDeliveryPercent"
                                id="againstDeliveryPercent"
                                type="text"
                                value={formValues.againstDeliveryPercent || "100"}
                                onChange={handleInputChange}
                                error={error?.againstDeliveryPercent}
                                setError={setError}
                                required={false}
                                readOnly={true}
                                mandatoryFields={mandatoryFields}
                                setMandatoryFields={setMandatoryFields}
                                fieldName="Against Delivery %"
                                isNumericWithDecimal
                                placeholder="Enter value"
                            />
                        </div>
                        <span className="payment-col-value">{paymentCalc.againstDeliveryINR}</span>
                    </div>
                    <div className="payment-details-row">
                        <span className="payment-col-item">Against installation*</span>
                        <div className="payment-col-percent">
                            <InputBox
                                label="Against Installation %"
                                name="againstInstallationPercent"
                                id="againstInstallationPercent"
                                type="text"
                                value={formValues.againstInstallationPercent || "0"}
                                onChange={handleInputChange}
                                error={error?.againstInstallationPercent}
                                setError={setError}
                                required={false}
                                readOnly={true}
                                mandatoryFields={mandatoryFields}
                                setMandatoryFields={setMandatoryFields}
                                fieldName="Against Installation %"
                                isNumericWithDecimal
                                placeholder="Enter value"
                            />
                        </div>
                        <span className="payment-col-value">{paymentCalc.againstInstallationINR}</span>
                    </div>
                    <div className="payment-details-row payment-details-total">
                        <span className="payment-col-item">Total Value</span>
                        <span className="payment-col-percent payment-total-value">{paymentCalc.totalPercent}%</span>
                        <span className="payment-col-value payment-total-value">{paymentCalc.totalINR}</span>
                    </div>
                    {error?.totalPaymentPercent && (
    <div className="error">{error.totalPaymentPercent}</div>
)}
                </div>
                <div className="payment-installation-charge">
                    <InputBox
                        label="Installation Charge*"
                        name="installationCharge"
                        id="installationCharge"
                        type="text"
                        value={formValues.installationCharge || ""}
                        onChange={handleInputChange}
                        error={error?.installationCharge}
                        setError={setError}
                        required={!readOnly}
                        readOnly={readOnly}
                        mandatoryFields={mandatoryFields}
                        setMandatoryFields={setMandatoryFields}
                        fieldName="Installation Charge"
                        isNumericWithDecimalLimit
                        maxLength={20}
                    />
                </div>
            </div>

            {/* LD Details */}
            <div className="add-edit-form isob-ld-details-section">
                <h6 className="section-subheading">LD Details</h6>
                <div className="order-info-section-colored-norightmar">
                    <div className="isob-dealer-sac-radio-row">
                        <Form.Label className="isob-dealer-sac-label">LD Applicable *</Form.Label>
                        <Form.Check
                            inline
                            type="radio"
                            label="Yes"
                            name="LD_applicable"
                            value="Yes"
                            checked={formValues.LD_applicable === "Yes"}
                            onChange={handleInputChange}
                            disabled={readOnly}
                        />
                        <Form.Check
                            inline
                            type="radio"
                            label="No"
                            name="LD_applicable"
                            value="No"
                            checked={formValues.LD_applicable !== "Yes"}
                            onChange={handleInputChange}
                            disabled={readOnly}
                        />
                    </div>
                    {formValues.LD_applicable === "Yes" && (
                        <>
                            <Row className="modal-row  isob-ld-row">
                                <InputBox
                                    label="LD Min *"
                                    name="LD_min"
                                    id="LD_min"
                                    type="text"
                                    value={formValues.LD_min || ""}
                                    onChange={handleInputChange}
                                    error={error?.LD_min}
                                    setError={setError}
                                    required={!readOnly}
                                    readOnly={readOnly}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName="LD Min"
                                    isNumericWithDecimal
                                    maxLength={20}
                                />
                                <InputBox
                                    label="LD Max *"
                                    name="ldMax"
                                    id="ldMax"
                                    type="text"
                                    value={formValues.ldMax || ""}
                                    onChange={handleInputChange}
                                    error={error?.ldMax}
                                    setError={setError}
                                    required={!readOnly}
                                    readOnly={readOnly}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName="LD Max"
                                    isNumericWithDecimal
                                    maxLength={20}
                                />
                            </Row>
                            <h6 className="section-subheading-plain">Delivery</h6>
                            <Row className="modal-row ">
                                <InputBox label="Delivery Start Date" name="deliveryStartDate" id="deliveryStartDate" type="date" value={formValues.deliveryStartDate || ""} onChange={handleInputChange} setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields}  fieldName="Delivery Start Date" />
                                <InputBox label="Delivery LD Per Week" name="deliveryLDPerWeek" id="deliveryLDPerWeek" type="text" value={formValues.deliveryLDPerWeek || ""} onChange={handleInputChange} setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields} error={error?.deliveryLDPerWeek} fieldName="Delivery LD Per Week" isNumericWithDecimal />
                                <InputBox label="Delivery End Date" name="deliveryEndDate" id="deliveryEndDate" type="date" value={formValues.deliveryEndDate || ""} onChange={handleInputChange} setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields} fieldName="Delivery End Date" />
                                <InputBox label="Delivery Max LD" name="deliveryMaxLD" id="deliveryMaxLD" type="text" value={formValues.deliveryMaxLD || ""} onChange={handleInputChange} error={error?.deliveryMaxLD} setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields} fieldName="Delivery Max LD" isNumericWithDecimal />
                            </Row>
                            <h6 className="section-subheading-plain">Installation</h6>
                            <Row className="modal-row ">
                                <InputBox label="Installation Start Date" name="instalStartDate" id="instalStartDate" type="date" value={formValues.instalStartDate || ""} onChange={handleInputChange} setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields} fieldName="Installation Start Date" />
                                <InputBox label="Installation LD Per Week" name="instalLDPerWeek" id="instalLDPerWeek" type="text" value={formValues.instalLDPerWeek || ""} onChange={handleInputChange} error={error?.instalLDPerWeek} setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields} fieldName="Installation LD Per Week" isNumericWithDecimal />
                                <InputBox label="Installation End Date" name="instalEndDate" id="instalEndDate" type="date" value={formValues.instalEndDate || ""} onChange={handleInputChange} setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields} fieldName="Installation End Date" />
                                <InputBox label="Installation Max LD" name="instalMaxLD" id="instalMaxLD" type="text" value={formValues.instalMaxLD || ""} onChange={handleInputChange} error={error?.instalMaxLD}  setError={setError} mandatoryFields={mandatoryFields} setMandatoryFields={setMandatoryFields} fieldName="Installation Max LD" isNumericWithDecimal />
                            </Row>
                        </>
                    )}
                </div>
            </div>

            {/* Add/Edit PG Details Modal */}
            <Modal show={showPgModal} centered>
                <Modal.Header>
                    <Modal.Title>{editPgIdx !== null ? "Edit" : "Add"} PG Details</Modal.Title>
                    <Image src={xclose} onClick={closePgModal} />
                </Modal.Header>
                <Modal.Body>
                    <Row className="modal-row add-edit-form">
                        <SearchSelect
                            label="Solution Offering *"
                            name="solutionOffering"
                            id="modal_pg_solutionOffering"
                            type="text"
                            option={(orderBookingFormData?.PG?.results || orderBookingFormData?.PG || []).map((pg, idx) => (
                                <option key={idx} value={pg}>{pg}</option>
                            ))}
                            value={pgForm.solutionOffering}
                            onChange={(e) => setPgForm({ ...pgForm, solutionOffering: e.target.value })}
                            required={!readOnly}
                            readOnly={readOnly}
                            fieldName="Solution Offering"
                            error={error.solutionOffering}
                            setError={setError}
                            mandatoryFields={mandatoryFields}
                            setMandatoryFields={setMandatoryFields}
                        />
                        <InputBox
                            label="BOM Price (INR) *"
                            name="bomPrice"
                            id="modal_pg_bomPrice"
                            type="text"
                            value={pgForm.bomPrice}
                            onChange={(e) => setPgForm({ ...pgForm, bomPrice: e.target.value })}
                            required={!readOnly}
                            readOnly={readOnly}
                            fieldName="BOM Price (INR)"
                            isNumericWithDecimal
                            maxLength={15}
                            error={error.bomPrice}
                            setError={setError}
                            mandatoryFields={mandatoryFields}
                            setMandatoryFields={setMandatoryFields}
                        />
                    </Row>
                    <Row className="modal-row add-edit-form">
                        <InputBox
                            label="Customer Order Basic Price(INR)*"
                            name="cobp"
                            id="modal_pg_cobp"
                            type="text"
                            value={pgForm.cobp}
                            onChange={(e) => setPgForm({ ...pgForm, cobp: e.target.value })}
                            required={!readOnly}
                            readOnly={readOnly}
                            fieldName="Customer Order Basic Price(INR)"
                            isNumericWithDecimal
                            maxLength={10}
                            error={error.cobp}
                            setError={setError}
                            mandatoryFields={mandatoryFields}
                            setMandatoryFields={setMandatoryFields}
                        />
                        <InputBox
                            label="Dealer Order Basic Price(INR)*"
                            name="dobp"
                            id="modal_pg_dobp"
                            type="text"
                            value={pgForm.dobp}
                            onChange={(e) => setPgForm({ ...pgForm, dobp: e.target.value })}
                            required={!readOnly}
                            readOnly={readOnly}
                            fieldName="Dealer Order Basic Price(INR)"
                             isNumericWithDecimal
                            maxLength={10}
                            error={error.dobp}
                            setError={setError}
                            mandatoryFields={mandatoryFields}
                            setMandatoryFields={setMandatoryFields}
                        />
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsPrefix="reset-btn" className="reset-btn-primary" onClick={() => {setShowPgModal(false);setError((prev) => ({ ...prev, solutionOffering: "", bomPrice: "", cobp: "", dobp: "" }))}}>Cancel</Button>
                    <Button variant="primary" className="btn-primary" onClick={handleSavePg} disabled={!pgForm.solutionOffering || !pgForm.bomPrice || !pgForm.cobp || !pgForm.dobp || (error.solutionOffering && error.solutionOffering != "") || (error.bomPrice && error.bomPrice != "") || (error.cobp && error.cobp != "") || (error.dobp && error.dobp != "")}>
                        {editPgIdx !== null ? "Update" : "Add"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
